<template>
  <div
    class="max-w-2xl rounded-lg border p-3 shadow-sm transition-colors duration-300"
    :class="isGray ? 'border-gray-200 bg-gray-100' : 'border-purple-200 bg-purple-100/60'"
  >
    <!-- Header row -->
    <div class="flex items-center gap-1.5">
      <Scale
        v-if="stage === 'view-verdicts'"
        class="h-5 w-5 shrink-0 text-gray-500"
      />
      <Check
        v-else-if="stage === 'enabled'"
        class="h-5 w-5 shrink-0 text-purple-700"
      />
      <Ban
        v-else-if="stage === 'declined'"
        class="h-5 w-5 shrink-0 text-gray-500"
      />
      <Lightbulb
        v-else
        class="h-5 w-5 shrink-0 text-purple-700"
      />

      <span
        class="flex-1 text-sm font-medium"
        :class="isGray ? 'text-gray-500' : 'text-purple-700'"
      >
        {{ headerText }}
      </span>

      <!-- Enabled: "View verdict sources" link -->
      <button
        v-if="stage === 'enabled'"
        class="flex items-center gap-0.5 text-sm font-medium text-purple-600 underline hover:no-underline"
        @click="reask"
      >
        {{ t('viewVerdictSources') }}
        <ChevronRight class="h-4 w-4 shrink-0" />
      </button>

      <!-- Declined / view-verdicts: × to fully close -->
      <button
        v-if="stage === 'declined' || stage === 'view-verdicts'"
        class="text-gray-400 hover:text-gray-700"
        @click="emit('close')"
      >
        <X class="h-4 w-4" />
      </button>

      <!-- Default: × to dismiss -->
      <button
        v-if="stage === 'default'"
        class="text-purple-400 hover:text-purple-600"
        @click="dismiss"
      >
        <X class="h-4 w-4" />
      </button>
    </div>

    <!-- Collapsible body (Default state only) -->
    <div
      class="grid transition-[grid-template-rows] duration-300 ease-in-out"
      :class="stage === 'default' ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
    >
      <div class="overflow-hidden">
        <div class="mt-3 rounded-lg border border-purple-200 bg-white p-3">
          <p class="mb-3 text-sm text-gray-600">{{ t('description') }}</p>
          <div class="flex items-center justify-end gap-2">
            <button
              class="rounded px-3 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-100"
              @click="dismiss"
            >
              {{ t('dismiss') }}
            </button>
            <button
              class="rounded bg-purple-600 px-4 py-1.5 text-sm text-white transition-colors hover:bg-purple-700"
              @click="enable"
            >
              {{ t('enable') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useTranslation } from '@/composables/useTranslation'
  import { Ban, Check, ChevronRight, Lightbulb, Scale, X } from 'lucide-vue-next'
  import { computed, ref } from 'vue'

  const emit = defineEmits<{
    enable: []
    reask: []
    close: []
  }>()

  const stage = ref<'default' | 'enabled' | 'declined' | 'view-verdicts'>('default')

  const isGray = computed(() => stage.value === 'declined' || stage.value === 'view-verdicts')

  const { t } = useTranslation({
    en: {
      titleDefault: 'Court database is currently disabled',
      titleEnabled: 'Court database enabled',
      titleDeclined: 'Court database suggestion dismissed',
      titleViewVerdicts: 'Court database enabled — retrieving relevant verdicts',
      description:
        'If you want AI Chat to support this answer with verified verdicts, you can enable the court database using the toggle below.',
      dismiss: 'Dismiss',
      enable: 'Enable court database',
      viewVerdictSources: 'View verdict sources'
    },
    de: {
      titleDefault: 'Urteilsdatenbank ist derzeit deaktiviert',
      titleEnabled: 'Urteilsdatenbank aktiviert',
      titleDeclined: 'Urteilsdatenbank-Vorschlag abgelehnt',
      titleViewVerdicts: 'Urteilsdatenbank aktiviert — relevante Urteile werden gesucht',
      description:
        'Wenn Sie möchten, dass der KI-Chat diese Antwort mit verifizierten Urteilen belegt, können Sie die Urteilsdatenbank über den Schalter unten aktivieren.',
      dismiss: 'Schließen',
      enable: 'Urteilsdatenbank aktivieren',
      viewVerdictSources: 'Urteilsquellen anzeigen'
    }
  })

  const headerText = computed(() => {
    if (stage.value === 'enabled') return t('titleEnabled')
    if (stage.value === 'declined') return t('titleDeclined')
    if (stage.value === 'view-verdicts') return t('titleViewVerdicts')
    return t('titleDefault')
  })

  const dismiss = () => {
    stage.value = 'declined'
  }

  const enable = () => {
    stage.value = 'enabled'
    emit('enable')
  }

  const reask = () => {
    stage.value = 'view-verdicts'
    emit('reask')
  }
</script>
